/* eslint-disable @typescript-eslint/no-magic-numbers */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, output, signal, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { EnhancePatientNotePromptType } from '@ffd/api-client';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { ContextMenuHeaderButton, GenerateTextAction, TooltipModule, VoiceTypingTextFieldComponent } from '@libs/ui-lib';
import { NoteSavingIndicatorComponent } from '@libs/notes';
import { selectIsEaglesoftPms } from '@libs/store';
import { HealthHistoryNotesService } from './health-history-notes.service';

@Component({
  selector: 'lib-health-history-notes',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    TranslateModule,
    ReactiveFormsModule,
    LottieComponent,
    VoiceTypingTextFieldComponent,
    TooltipModule,
    NoteSavingIndicatorComponent,
  ],
  templateUrl: './health-history-notes.component.html',
  styleUrl: './health-history-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HealthHistoryNotesComponent implements OnDestroy {
  private readonly healthHistoryNotesService = inject(HealthHistoryNotesService);
  private readonly store = inject(Store);

  protected readonly isEaglesoftPms = this.store.selectSignal(selectIsEaglesoftPms);

  public readonly noteClicked = output();

  public readonly voiceTypingTextField = viewChild<VoiceTypingTextFieldComponent>('voiceTypingTextField');

  public controlledNote$ = this.healthHistoryNotesService.controlledNote$;
  public undoDisabledSignal = this.healthHistoryNotesService.undoDisabledSignal;
  public redoDisabledSignal = this.healthHistoryNotesService.redoDisabledSignal;
  public isGeneratingSignal = this.healthHistoryNotesService.isGeneratingSignal;
  public currentHistoryElementSignal = this.healthHistoryNotesService.currentHistoryElementSignal;
  public lastFocusedCursorOffsetSignal = this.healthHistoryNotesService.lastFocusedCursorOffsetSignal;
  public readonly transcription = this.healthHistoryNotesService.transcriptionControl;
  public readonly dictationFinalized$ = this.healthHistoryNotesService.dictationFinalized$;
  public readonly transcriptionStatus = this.healthHistoryNotesService.transcriptionStatus;
  public readonly transcriptionStatus$ = toObservable(this.healthHistoryNotesService.transcriptionStatus);

  protected isMicSettingsOpen = signal(false);
  public isEdited = signal(false);

  protected readonly isPatientNotesSavingInProgress = computed(() => this.healthHistoryNotesService.isPatientNotesSavingInProgress());
  protected readonly isPatientNotesSaved = computed(() => this.healthHistoryNotesService.isPatientNotesSaved());
  protected readonly patientNotes = computed(() => this.healthHistoryNotesService.patientNotes());
  protected readonly patientNotesLoading = computed(() => this.healthHistoryNotesService.patientNotesLoading());

  public EnhancePatientNotesPromptType = EnhancePatientNotePromptType;

  protected contextMenuHeaderConfig = signal<ContextMenuHeaderButton[]>([
    {
      text: 'health_history_notes_section_rewrite_enhance',
      type: GenerateTextAction.HealthHistoryRewrite,
    },
    {
      text: 'health_history_notes_section_concise_enhance',
      type: GenerateTextAction.HealthHistoryConcise,
    },
    {
      text: 'health_history_notes_section_bullets_enhance',
      type: GenerateTextAction.HealthHistoryBullets,
    },
  ]);

  protected loadingLottieOptions: AnimationOptions = {
    path: 'assets/animations/loading-spinner.json',
  };
  protected enhanceLottieOptions: AnimationOptions = {
    path: 'assets/animations/enhance.json',
  };

  protected onPatientNotesClick(event: MouseEvent): void {
    event.stopPropagation();
    this.isEdited.set(true);
    this.healthHistoryNotesService.stopVoiceTyping();
    this.noteClicked.emit();
    this.healthHistoryNotesService.init();
  }

  public updateLastFocusedCursorOffset(offset: number): void {
    this.healthHistoryNotesService.updateLastFocusedCursorOffset(offset);
  }

  public ngOnDestroy(): void {
    if (this.isGeneratingSignal()) {
      this.healthHistoryNotesService.abortEnhance();
    }
    this.healthHistoryNotesService.stopVoiceTyping();
    this.healthHistoryNotesService.resetHistory();
  }

  public onDictationClick(): void {
    this.healthHistoryNotesService.toggleSpeechRecognitionListening();
  }

  public enhanceNote(enhanceType: EnhancePatientNotePromptType): void {
    this.healthHistoryNotesService.enhanceNote({
      noteInput: this.voiceTypingTextField()?.getTemplateText() ?? '',
      enhanceType,
    });
  }

  public onRecordingLoading(note: string): void {
    this.healthHistoryNotesService.setControlledNote(note);
  }

  public onRecordingStopped(note: string): void {
    this.healthHistoryNotesService.commitDictatedText(note);
  }

  public redo(): void {
    this.healthHistoryNotesService.redo();
    this.voiceTypingTextField()?.regenerate();
  }

  public undo(): void {
    this.healthHistoryNotesService.undo();
    this.voiceTypingTextField()?.regenerate();
  }

  public onManualTypeKeyUp(text: string): void {
    this.healthHistoryNotesService.noteTyping({ notes: text });
  }

  public cancelEdit(): void {
    this.isEdited.set(false);
    this.healthHistoryNotesService.stopVoiceTyping();
    this.healthHistoryNotesService.resetHistory();
  }

  public onGenerateTextFromContextMenu({
    action,
    text,
    textStart,
    textEnd,
  }: {
    action: GenerateTextAction;
    text: string;
    textStart: number;
    textEnd: number;
  }): void {
    this.healthHistoryNotesService.enhanceNote({
      noteInput: text,
      enhanceType: this.mapEnhancePromptType(action),
      textStart,
      textEnd,
      fullNote: this.voiceTypingTextField()?.getTemplateText() ?? '',
    });
  }

  private mapEnhancePromptType(action: GenerateTextAction): EnhancePatientNotePromptType {
    switch (action) {
      case GenerateTextAction.HealthHistoryBullets: {
        return EnhancePatientNotePromptType.BULLETS;
      }
      case GenerateTextAction.HealthHistoryConcise: {
        return EnhancePatientNotePromptType.CONCISE;
      }
      case GenerateTextAction.HealthHistoryRewrite: {
        return EnhancePatientNotePromptType.REWRITE;
      }
      default: {
        return EnhancePatientNotePromptType.REWRITE;
      }
    }
  }
}
